import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { format } from "date-fns";
import SEOHead from "./SEOHead";
import { createBlogPostingSchema } from "../data/seoSchemas";
import { api } from "../services/api";
import "./BlogDetail.css";

const BlogDetail = () => {
  const { id } = useParams();
  const [blog, setBlog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    const fetchBlog = async () => {
      setLoading(true);
      try {
        const data = await api.getArticle(id);
        setBlog(data);
      } catch (err) {
        setError("Article not found or temporarily unavailable.");
      } finally {
        setLoading(false);
      }
    };
    fetchBlog();
  }, [id]);

  if (loading) {
    return (
      <div className="text-center py-5">
        <SEOHead title="Loading Clinical Article | Dr. Sunil Apsingi" noindex={true} />
        <div className="spinner-border text-teal" role="status">
          <span className="visually-hidden">Loading article...</span>
        </div>
        <p className="text-muted mt-2 small">Loading article details...</p>
      </div>
    );
  }

  if (error || !blog) {
    return (
      <div className="container py-5 text-center">
        <SEOHead title="Article Not Found | Dr. Sunil Apsingi" noindex={true} />
        <div className="alert alert-warning d-inline-block px-4 py-3">
          <i className="fa-solid fa-triangle-exclamation me-2"></i>
          {error || "Article could not be loaded."}
        </div>
        <div className="mt-3">
          <Link to="/blogs" className="btn-medical-primary">
            ← Return to Articles
          </Link>
        </div>
      </div>
    );
  }

  const publishDate = blog.datetime ? new Date(blog.datetime) : new Date();
  const rawExcerpt = blog.content ? blog.content.replace(/<[^>]*>?/gm, "").trim() : "";
  const metaDescription = rawExcerpt.length > 155 ? `${rawExcerpt.slice(0, 152)}...` : (rawExcerpt || `Orthopedic clinical insight on ${blog.title} by Dr. Sunil Apsingi.`);
  const blogSchema = createBlogPostingSchema(blog);

  return (
    <article className="article-page section-padding">
      <SEOHead
        title={`${blog.title} | Dr. Sunil Apsingi`}
        description={metaDescription}
        canonicalPath={`/blogs/${blog.id}`}
        ogType="article"
        ogImage={blog.poster || "/images/2022-05-13.png"}
        schemaJson={blogSchema}
        breadcrumbs={[
          { name: "Home", path: "/" },
          { name: "Articles", path: "/blogs" },
          { name: blog.title, path: `/blogs/${blog.id}` },
        ]}
      />
      <div className="container">
        {/* Navigation Breadcrumb */}
        <div className="breadcrumb-row mb-4">
          <Link to="/" className="crumb-link">Home</Link>
          <span className="crumb-sep">/</span>
          <Link to="/blogs" className="crumb-link">Articles</Link>
          <span className="crumb-sep">/</span>
          <span className="crumb-current">{blog.title}</span>
        </div>

        <div className="row justify-content-center">
          <div className="col-lg-9 col-md-11">
            {/* Header Block */}
            <header className="article-header mb-4">
              <span className="specialist-badge mb-2">{blog.specialist || "Orthopedic Care"}</span>
              <h1 className="article-headline mb-3">{blog.title}</h1>

              <div className="article-meta-row d-flex flex-wrap align-items-center gap-3">
                <div className="d-flex align-items-center gap-2">
                  <img
                    src="/images/2022-05-13.png"
                    alt={blog.author || "Dr. Sunil Apsingi"}
                    className="meta-author-avatar"
                  />
                  <div>
                    <strong className="d-block small text-navy">{blog.author || "Dr. Sunil Apsingi"}</strong>
                    <small className="text-muted">Senior Orthopedic Surgeon</small>
                  </div>
                </div>
                <div className="meta-divider d-none d-sm-block"></div>
                <div className="meta-time small text-muted">
                  <i className="fa-regular fa-calendar me-1 text-teal"></i>
                  {format(publishDate, "MMMM d, yyyy")}
                </div>
              </div>
            </header>

            {/* Featured Poster */}
            {blog.poster && (
              <div className="article-hero-poster mb-5">
                <img
                  src={blog.poster}
                  alt={blog.title}
                  className="w-100 rounded-4 shadow-sm"
                />
              </div>
            )}

            {/* Rich Text Body */}
            <div
              className="article-rich-content mb-5"
              dangerouslySetInnerHTML={{ __html: blog.content }}
            />

            {/* Author Credential Card & Next Steps CTA */}
            <div className="article-author-callout p-4 rounded-4 mb-5">
              <div className="d-flex flex-wrap align-items-center gap-4">
                <img
                  src="/images/2022-05-13.png"
                  alt="Dr. Sunil Apsingi"
                  className="author-callout-photo"
                />
                <div className="flex-grow-1">
                  <h4 className="mb-1 text-navy">Authored by Dr. Sunil Apsingi</h4>
                  <p className="text-teal font-monospace small mb-2">MS (PGI Chandigarh) • Senior Joint Specialist</p>
                  <p className="small text-secondary mb-3">
                    Have symptoms or questions regarding this condition? Schedule an in-person clinical assessment at Medicover OPC, HITEC City.
                  </p>
                  <Link to="/bookappointment" className="btn-medical-primary">
                    Book Consultation with Dr. Sunil
                  </Link>
                </div>
              </div>
            </div>

            {/* Return Link */}
            <div className="text-center pt-3">
              <Link to="/blogs" className="btn-medical-secondary">
                <i className="fa-solid fa-arrow-left me-2"></i> Browse All Articles
              </Link>
            </div>
          </div>
        </div>
      </div>
    </article>
  );
};

export default BlogDetail;